// apps/web/src/pages/attendance/AttendancePage.tsx
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import api from '../../lib/api'

interface AttendanceRecord {
  id: string
  technicianId: string
  date: string
  workedMinutes: number
  source: string
}

interface User {
  id: string
  firstName: string
  lastName: string
  branchId: string | null
  role?: string
}

interface LeaveRequest {
  id: string
  technicianId: string
  dateFrom: string
  dateTo: string
  status: string
  leaveType: { name: string; color: string }
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

export function AttendancePage() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth()) // 0-based
  const [exportLoading, setExportLoading] = useState(false)
  const [exportTechId, setExportTechId] = useState<string>('all')

  const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`
  const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${getDaysInMonth(year, month)}`

  const { data: technicians = [] } = useQuery<User[]>({
    queryKey: ['users', 'my-team'],
    queryFn: async () => (await api.get<User[]>('/users/my-team')).data,
  })

  const { data: records = [] } = useQuery<AttendanceRecord[]>({
    queryKey: ['attendance', dateFrom, dateTo],
    queryFn: async () => (await api.get<AttendanceRecord[]>('/attendance', { params: { dateFrom, dateTo } })).data,
  })

  const { data: leaves = [] } = useQuery<LeaveRequest[]>({
    queryKey: ['leave-requests', dateFrom, dateTo],
    queryFn: async () => (await api.get<LeaveRequest[]>('/leave-requests', { params: { dateFrom, dateTo } })).data,
  })

  const daysCount = getDaysInMonth(year, month)
  const days = Array.from({ length: daysCount }, (_, i) => i + 1)

  const recordMap = useMemo(() => {
    const m = new Map<string, AttendanceRecord>()
    records.forEach((r) => m.set(`${r.technicianId}-${r.date}`, r))
    return m
  }, [records])

  const leavesByTech = useMemo(() => {
    const m = new Map<string, LeaveRequest[]>()
    leaves.forEach((l) => {
      const arr = m.get(l.technicianId) ?? []
      arr.push(l)
      m.set(l.technicianId, arr)
    })
    return m
  }, [leaves])

  function getCellState(techId: string, day: number) {
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const hasPresence = recordMap.has(`${techId}-${date}`)
    const techLeaves = leavesByTech.get(techId) ?? []
    const leave = techLeaves.find((l) => l.dateFrom <= date && date <= l.dateTo && l.status === 'approved')
    const pendingLeave = techLeaves.find((l) => l.dateFrom <= date && date <= l.dateTo && l.status === 'pending')
    if (leave) return { bg: leave.leaveType.color + '33', border: leave.leaveType.color, label: leave.leaveType.name[0] }
    if (pendingLeave) return { bg: '#FEF9C3', border: '#EAB308', label: '?' }
    if (hasPresence) return { bg: '#DCFCE7', border: '#16A34A', label: 'P' }
    return null
  }

  async function handleExport(format: 'xlsx' | 'pdf') {
    setExportLoading(true)
    try {
      const params: Record<string, string> = { format, dateFrom, dateTo }
      if (exportTechId !== 'all') params['technicianId'] = exportTechId
      const resp = await api.get('/attendance/export', { params, responseType: 'blob' })
      const url = URL.createObjectURL(resp.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `presenze_${dateFrom}_${dateTo}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
      a.click()
      URL.revokeObjectURL(url)
    } finally { setExportLoading(false) }
  }

  function prevMonth() {
    if (month === 0) { setYear((y) => y - 1); setMonth(11) }
    else setMonth((m) => m - 1)
  }
  function nextMonth() {
    if (month === 11) { setYear((y) => y + 1); setMonth(0) }
    else setMonth((m) => m + 1)
  }

  const monthName = new Date(year, month).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Presenze Team"
        breadcrumb={[{ label: 'Presenze' }]}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={exportTechId}
              onChange={(e) => setExportTechId(e.target.value)}
              className="text-sm rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] text-[var(--fc-foreground)] px-2 py-1.5"
            >
              <option value="all">Tutti i lavoratori</option>
              {technicians
                .filter((t) => ['technician', 'team_leader'].includes(t.role ?? ''))
                .map((t) => (
                  <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>
                ))}
            </select>
            <Button size="sm" variant="secondary" onClick={() => void handleExport('xlsx')} disabled={exportLoading}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void handleExport('pdf')} disabled={exportLoading}>
              <Download className="h-4 w-4 mr-1" /> PDF Kleiss
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={prevMonth}>{'<'}</Button>
        <span className="font-semibold capitalize text-[var(--fc-foreground)]">{monthName}</span>
        <Button variant="ghost" size="sm" onClick={nextMonth}>{'>'}</Button>
      </div>

      <div className="flex gap-4 text-xs text-[var(--fc-foreground-muted)]">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#DCFCE7] border border-[#16A34A]" /> Presente</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#FEF9C3] border border-[#EAB308]" /> Permesso pending</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#DBEAFE] border border-[#3B82F6]" /> Permesso approvato</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[var(--fc-border)]">
        <table className="text-xs min-w-full">
          <thead className="bg-[var(--fc-surface-overlay)]">
            <tr>
              <th className="sticky left-0 bg-[var(--fc-surface-overlay)] px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)] min-w-[140px]">Tecnico</th>
              {days.map((d) => (
                <th key={d} className="px-1 py-2 text-center font-medium text-[var(--fc-foreground-muted)] min-w-[28px]">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--fc-border)]">
            {technicians.filter((t) => ['technician', 'team_leader'].includes(t.role ?? '')).map((tech) => (
              <tr key={tech.id} className="hover:bg-[var(--fc-surface-overlay)]">
                <td className="sticky left-0 bg-[var(--fc-surface)] px-3 py-1 font-medium text-[var(--fc-foreground)] whitespace-nowrap">
                  {tech.firstName} {tech.lastName}
                </td>
                {days.map((d) => {
                  const cell = getCellState(tech.id, d)
                  return (
                    <td key={d} className="px-0.5 py-1 text-center">
                      {cell ? (
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold"
                          style={{ backgroundColor: cell.bg, border: `1px solid ${cell.border}`, color: cell.border }}
                          title={cell.label}
                        >
                          {cell.label}
                        </span>
                      ) : (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground-muted)]">·</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
